import { Fragment, forwardRef, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Segmented.module.scss'

export type SegmentedSize = 'sm' | 'md'
export type SegmentedVariant = 'default' | 'compact'

export interface SegmentedOption<T extends string = string> {
  value: T
  label?: ReactNode
  icon?: ReactNode
  'aria-label'?: string
  /** When true, the item is rendered but not interactive. */
  disabled?: boolean
  /** Optional text surfaced by the browser's native title tooltip. */
  title?: string
  /**
   * Wraps the rendered item, e.g. in a menu trigger (``asChild``) that opens
   * on hover. The item forwards its ref and props, so a Radix trigger works.
   */
  wrap?: (item: ReactElement) => ReactNode
}

export interface SegmentedProps<T extends string = string> {
  options: ReadonlyArray<SegmentedOption<T>>
  value: T
  onValueChange: (value: T) => void
  size?: SegmentedSize
  variant?: SegmentedVariant
  className?: string
  /** Accessible label for the whole control (announced by SR). */
  'aria-label'?: string
}

export function Segmented<T extends string = string>({
  options,
  value,
  onValueChange,
  size = 'md',
  variant = 'default',
  className,
  'aria-label': ariaLabel,
}: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(styles.group, styles[`size-${size}`], styles[`variant-${variant}`], className)}
    >
      {options.map((opt) => {
        const item = (
          <SegmentedItem
            key={opt.value}
            selected={opt.value === value}
            disabled={opt.disabled}
            title={opt.title}
            onClick={() => {
              if (!opt.disabled) onValueChange(opt.value)
            }}
            aria-label={opt['aria-label']}
          >
            {opt.icon ? <span className={styles.icon} aria-hidden>{opt.icon}</span> : null}
            {opt.label}
          </SegmentedItem>
        )
        return opt.wrap ? <Fragment key={opt.value}>{opt.wrap(item)}</Fragment> : item
      })}
    </div>
  )
}

interface SegmentedItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected: boolean
}

const SegmentedItem = forwardRef<HTMLButtonElement, SegmentedItemProps>(function SegmentedItem(
  { selected, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      role="radio"
      aria-checked={selected}
      type={type ?? 'button'}
      className={cn(styles.item, selected && styles.selected, className)}
      {...rest}
    >
      {children}
    </button>
  )
})
